import { Button, DirectoryTable, ErrorAlert, Notice, PageHeader, useFormatters } from '@lumlab/platform-ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { apiRequest } from '@lumlab/platform-product/web';
import type { RejectedAddress } from '../contract/index.ts';
import { useDirectory } from './queries.ts';

const path = '/api/admin/mail/rejected';

/** Addresses the receiving server refused for good: nothing is sent to them until an administrator allows them. */
export function MailPage() {
    const { t } = useTranslation();
    const format = useFormatters();
    const client = useQueryClient();
    const addresses = useDirectory<RejectedAddress>(path);
    const allow = useMutation({
        mutationFn: (email: string) => apiRequest(`${path}/allow`, { method: 'POST', body: { email } }),
        onSuccess: () => client.invalidateQueries({ queryKey: ['admin', path] }),
    });
    return (
        <>
            <PageHeader title={t('platform.admin.mail')} description={t('platform.admin.mailDescription')} />
            {allow.error && <ErrorAlert error={allow.error} />}
            {allow.isSuccess && <Notice tone="success">{t('platform.admin.addressAllowed')}</Notice>}
            <DirectoryTable
                items={(addresses.data?.items ?? []).map((address) => ({ ...address, id: address.email }))}
                total={addresses.data?.total ?? 0}
                loading={addresses.isPending}
                error={addresses.error}
                search={addresses.search}
                onSearchChange={addresses.setSearch}
                page={addresses.page}
                pageSize={addresses.pageSize}
                onPageChange={addresses.setPage}
                columns={[
                    { key: 'email', label: t('platform.auth.email'), render: (address) => address.email },
                    {
                        key: 'rejectedAt',
                        label: t('platform.admin.rejectedAt'),
                        render: (address) => format.dateTime(new Date(address.rejectedAt)),
                    },
                    {
                        key: 'actions',
                        label: t('platform.admin.actions'),
                        render: (address) => (
                            <Button
                                variant="light"
                                size="xs"
                                disabled={allow.isPending}
                                onClick={() => allow.mutate(address.email)}
                                aria-label={t('platform.admin.allowAddressFor', { email: address.email })}
                            >
                                {t('platform.admin.allowAddress')}
                            </Button>
                        ),
                    },
                ]}
            />
        </>
    );
}
